<template>
  <sp-select
    v-bind="forwardedAttrs"
    ref="container"
    inputmode="search"
    :items="items"
    :item-title="itemTitle"
    :item-value="itemValue"
    :return-object="isReturnObject"
    :multiple="isMultiple"
    :hide-no-data="hidesNoData"
    :item-test-id="itemTestId"
    :highlight-first-result="highlightsFirstResult"
    :menu="menuAllowed"
    :disabled="disabled"
    :value.prop="model"
    @input="onInput"
    @text-input="onSearchInput"
    @update-menu="onMenuUpdate"
    @click-append="$emit('click-append')"
    @click-prepend="$emit('click-prepend')"
  >
    <slot slot="prepend" name="prepend"></slot>
    <slot slot="append" name="append"></slot>
    <slot slot="no-data" name="no-data">{{ noDataText }}</slot>
  </sp-select>
</template>

<script setup>
import { onClickOutside } from "@vueuse/core";
import { computed, ref, toRef, useAttrs, useTemplateRef, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useHostElement } from "#ce/composables/useHostElement";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { t } from "#ce/shared/forms-i18n";
import { useFormControl } from "#ce/composables/useFormControl";
import { isNoSelection, isPresent, toArray, toBoolean } from "#ce/utils/props";
import { isObjectLike } from "#ce/utils/types";
import { props as autocompleteProps } from "./props.js";
import { formAssociatedHost } from "#ce/framework/utils";

// sp-autocomplete is a genuine form control: match :disabled and respond to <fieldset disabled>, forwarding to its select.
// Attributes are forwarded explicitly rather than inherited, so `value` can be withheld.
defineOptions(formAssociatedHost({ inheritAttrs: false }));

const emit = defineEmits(["input", "click-append", "click-prepend", "update-search"]);
const { emitBubbling } = useHostEmit();
const host = useHostElement();

const props = defineProps(autocompleteProps);

// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, forwarded to the inner sp-select.
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

// The `value` attribute seeds the selection and names the value a reset restores; the `value`
// property is the live selection. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();

const isReturnObject = computed(() => toBoolean(props.returnObject));
const isMultiple = computed(() => toBoolean(props.multiple));

// The selection is an array while `multiple` and a single entry otherwise, whichever channel wrote
// it. Nothing selected is `[]` while `multiple` and `null` otherwise.
function normalizeSelection(value, multiple = isMultiple.value) {
  if (isNoSelection(value)) {
    return multiple ? [] : null;
  }

  return multiple ? toArray(value) : value;
}

// The `multiple` the host asks for, read from the attribute: a property write reflects there at
// once, while the prop follows in a later tick. Cast the way a `[Boolean, String]` prop is.
function requestedMultiple() {
  if (!host) {
    return isMultiple.value;
  }

  const raw = host.getAttribute("multiple");

  return raw === "" || raw === "multiple" || toBoolean(raw);
}

// The mode the live selection conforms to.
const selectionMode = ref(isMultiple.value);

function writeSelection(value, multiple = isMultiple.value) {
  model.value = normalizeSelection(value, multiple);
  selectionMode.value = multiple;
}

// The shape follows `multiple` when it changes after mount. The prior single value becomes one
// entry, an array included; leaving `multiple` keeps the first entry. A selection already written
// under the new mode keeps its shape.
watch(isMultiple, (multiple) => {
  if (multiple === selectionMode.value) {
    return;
  }

  if (multiple) {
    model.value = isNoSelection(model.value) ? [] : [model.value];
  } else if (Array.isArray(model.value)) {
    model.value = normalizeSelection(model.value[0]);
  }

  selectionMode.value = multiple;
});

const defaultValue = ref(attrs.value ?? null);
const model = ref(normalizeSelection(defaultValue.value));

watch(
  () => attrs.value,
  (value) => {
    defaultValue.value = value ?? null;
    writeSelection(defaultValue.value);
  },
);

// `value` is withheld: the select exposes a live `value` property, and Vue writes a fallthrough key
// that exists on a custom element as a property, so it would overwrite the selection.
const forwardedAttrs = computed(() => {
  const forwarded = { ...attrs };

  delete forwarded.value;

  return forwarded;
});

const { exposeProperties } = useExpose();

exposeProperties({
  type: {
    configurable: true,
    get: () => "text",
  },
  value: {
    configurable: true,
    get: () => model.value,
    set: (value) => {
      writeSelection(value, requestedMultiple());
    },
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

// The inner sp-select sits in the shadow root, where the outer form cannot see it, so the element
// submits the selection through ElementInternals under its own name.
const { emitChange, mirrorFormValue, mirrorValueMissing, onFormReset, name: formName } = useForm();
// `item-value` names a key on one item; an array is never one item.
function entryValue(entry) {
  const unwraps = isReturnObject.value && isObjectLike(entry) && !Array.isArray(entry);
  const value = unwraps ? entry[props.itemValue] : entry;

  return value === undefined || value === null ? "" : String(value);
}

// The entries of a multiple selection. One whose submitted value is empty does not count and is not
// submitted: `""`, `null`, and an item missing its `item-value` key under `return-object`.
const selectedEntries = computed(() => {
  if (isNoSelection(model.value)) {
    return [];
  }

  return toArray(model.value).filter((entry) => entryValue(entry) !== "");
});

// Without `multiple` the whole live value is one selection, an array or an object included.
const hasSelection = computed(() =>
  isMultiple.value ? selectedEntries.value.length > 0 : entryValue(model.value) !== "",
);

// A native <select multiple> submits one entry per selected option and nothing when none is
// selected, so the multiple case hands setFormValue a FormData, or null. Without a name there is
// nothing to submit under.
const formValue = computed(() => {
  if (!isMultiple.value) {
    return entryValue(model.value);
  }

  if (!formName.value || !hasSelection.value) {
    return null;
  }

  const data = new FormData();
  selectedEntries.value.forEach((entry) => data.append(formName.value, entryValue(entry)));

  return data;
});

mirrorFormValue(formValue);

onFormReset(() => {
  writeSelection(defaultValue.value);
  // The search is dropped too: while it filters the list to nothing the inner select cannot
  // resolve the restored selection's title, and the field keeps the typed text.
  clearSearch();
});

// A required autocomplete is satisfied only once something is selected.
const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("select_value_missing"));

mirrorValueMissing(() => isRequired.value && !hasSelection.value, validationMessage);

const search = ref(props.search);
watch(
  () => props.search,
  (value) => (search.value = value),
);

const hasSearchInput = computed(() => search.value?.length > 0);

// A pick under `clear-on-select` drops the search but not the picker; the picker's own close ends it.
const menuHeld = ref(false);
const menuAllowed = computed(() => hasSearchInput.value || menuHeld.value);

// The empty-state row shows only for a search that matches nothing.
const hidesNoData = computed(() => toBoolean(props.hideNoData) || !hasSearchInput.value);

const highlightsFirstResult = computed(() => toBoolean(props.autoSelectFirst) && hasSearchInput.value);

const noFilter = computed(() => toBoolean(props.noFilter));

// A primitive item is a row whose title and value are the item itself, in the object shape the
// picker's highlight and commit paths read. The row remembers its primitive, which `return-object`
// hands back in place of the row.
const primitiveOfRow = new WeakMap();

function normalizeItem(item) {
  if (isObjectLike(item)) {
    return item;
  }

  const row = { [props.itemTitle]: item, [props.itemValue]: item };
  primitiveOfRow.set(row, item);

  return row;
}

function denormalize(value) {
  if (Array.isArray(value)) {
    return value.map(denormalize);
  }

  return isObjectLike(value) && primitiveOfRow.has(value) ? primitiveOfRow.get(value) : value;
}

const items = computed(() => {
  const rows = props.items.filter((item) => item != null).map(normalizeItem);

  if (noFilter.value || !hasSearchInput.value) {
    return rows;
  }
  const query = search.value.toLowerCase();

  // A row without a string title, a header row included, never matches.
  return rows.filter((item) => {
    const title = item[props.itemTitle];
    return typeof title === "string" && title.toLowerCase().includes(query);
  });
});

// The only path that emits `input`, matching a native control, which reports user input alone: a
// value carried in by the attribute, the property or a reset is mirrored silently.
function onInput({ detail }) {
  const [value] = detail;
  writeSelection(denormalize(value));
  emitBubbling("input", model.value);
  // The inner select's own `change` is `composed: false`, so it stops at this element's shadow
  // boundary; the pick is reported to a consumer from here.
  emitChange();

  if (toBoolean(props.clearOnSelect)) {
    menuHeld.value = true;
    clearSearch();
  }
}

// Reports the user's typing. `clearSearch` reports the drop, and a `search` prop sync mirrors
// silently, so `update-search` carries every change the host does not already own.
function onSearchInput({ detail }) {
  const [value] = detail;
  search.value = value;
  emit("update-search", value);
}

// The search that filtered the list is dropped whenever the picker closes, so a reopened picker
// offers the full list rather than one narrowed by text the field no longer shows.
function onMenuUpdate({ detail }) {
  const [open] = detail;

  if (!open) {
    menuHeld.value = false;
    clearSearch();
  }
}

// Reported to the host, which owns the item list under `no-filter` and would otherwise keep serving
// the dropped query's results to a field that no longer shows it.
function clearSearch() {
  if (search.value == null) {
    return;
  }

  search.value = null;
  emit("update-search", null);
}

// A click away with the picker already closed reports no menu change, so the search is dropped here
// too.
const container = useTemplateRef("container");
onClickOutside(container, clearSearch);
</script>

<style>
:host {
  display: block;
}
</style>
